import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";
import {
  ContractDocumentsPage,
  ContractSearchResponse,
} from "./ContractSearchTypes";

/** The AI list endpoint's page ceiling; core clamps anything above it. */
export const CONTRACT_DOCUMENTS_PAGE_SIZE = 200;

/**
 * A search is an LLM round trip. Core gives the AI 30 s before it fails the
 * request; the browser waits a little longer so the server's message, not a
 * client timeout, is what the user sees.
 */
const SEARCH_TIMEOUT_MS = 35_000;

export const fetchContractDocuments = async (
  limit: number = CONTRACT_DOCUMENTS_PAGE_SIZE,
  offset: number = 0,
): Promise<ContractDocumentsPage> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.CONTRACT_DOCUMENTS}?limit=${limit}&offset=${offset}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Every page of the user's documents, plus the API's own `total` -- so the count on screen is the
 * service's answer rather than a number worked out from the rows.
 *
 * Stops early if the service ever fails to make progress, so a bad `hasMore` can never loop.
 */
export const fetchAllContractDocuments = async (): Promise<{
  documents: ContractDocumentsPage["documents"];
  total: number;
}> => {
  const all: ContractDocumentsPage["documents"] = [];
  let total = 0;
  let offset = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = await fetchContractDocuments(
      CONTRACT_DOCUMENTS_PAGE_SIZE,
      offset,
    );
    all.push(...(page.documents ?? []));
    if (typeof page.total === "number") total = page.total;
    if (!page.hasMore || !page.documents?.length) break;
    offset += page.documents.length;
  }
  // No total from the service (an older response shape) -> fall back to what came back.
  return { documents: all, total: total || all.length };
};

/**
 * One question. `conversationId` is the id an earlier answer returned; leave it out (null) to start a
 * new conversation. The server keeps the history, so only the new question is ever sent.
 */
export const searchContracts = async (
  query: string,
  conversationId: string | null = null,
): Promise<ContractSearchResponse> => {
  try {
    const body: { query: string; conversationId?: string } = { query };
    if (conversationId) body.conversationId = conversationId;
    const callParams = await getCallParams("POST", body);
    // Contract text is customer data: the PostHog capture option is deliberately not set here.
    const response = await makeCall(urls.CONTRACT_SEARCH, callParams, true, {
      timeoutMs: SEARCH_TIMEOUT_MS,
    });
    return response;
  } catch (error: any) {
    throw error;
  }
};
